import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Field, Pill, Spinner, TextArea } from "../components/ui";
import AppHeader from "../components/AppHeader";
import { Video, X, CalendarClock } from "lucide-react";
import { formatDate } from "../lib/dates";

const STATUSES = ["scheduled", "completed", "cancelled", "rescheduled"];

export default function MeetingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);

  // Reschedule sub-form state
  const [rescheduling, setRescheduling] = useState(false);
  const [rDate, setRDate] = useState("");
  const [rTime, setRTime] = useState("");
  const [slots, setSlots] = useState({ allowed: [], booked: [], blocked: [] });
  const [rError, setRError] = useState("");
  const [rBusy, setRBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const all = await api.get("/meetings");
      const r = all.find((x) => x.meeting_id === id);
      if (!r) { alert("Not found"); navigate(-1); return; }
      setM(r); setNotes(r.notes || ""); setSummary(r.summary || "");
      setRDate(r.date); setRTime(r.time);
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!rescheduling || !rDate) return;
    api.get(`/meetings/slots?date=${rDate}`)
      .then((r) => setSlots({ allowed: r.allowed || [], booked: r.booked || [], blocked: r.blocked || [] }))
      .catch(() => setSlots({ allowed: [], booked: [], blocked: [] }));
  }, [rescheduling, rDate]);

  const update = async (patch) => {
    setSaving(true);
    try { setM(await api.patch(`/meetings/${id}`, patch)); }
    catch (e) { alert(e?.message); }
    finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this meeting?")) return;
    await api.delete(`/meetings/${id}`);
    navigate("/meetings");
  };

  const startReschedule = () => {
    setRError("");
    setRDate(m.date);
    setRTime(m.time);
    setRescheduling(true);
  };

  const submitReschedule = async () => {
    setRError("");
    if (!rDate || !rTime) { setRError("Pick a date and a slot."); return; }
    if (rDate === m.date && rTime === m.time) { setRError("Choose a different date or time to reschedule."); return; }
    setRBusy(true);
    try {
      const updated = await api.patch(`/meetings/${id}`, { date: rDate, time: rTime, status: "rescheduled" });
      setM(updated);
      setRescheduling(false);
    } catch (e) {
      setRError(e?.message || "Could not reschedule");
    } finally {
      setRBusy(false);
    }
  };

  if (loading || !m) return <><AppHeader title="Meeting" back /><div className="sg-main"><Spinner /></div></>;

  const taken = new Set([
    ...(slots.booked || []),
    ...(slots.blocked || []),
  ]);
  // Allow keeping the current slot even if "taken" (because it's this meeting itself).
  taken.delete(m.time);

  return (
    <>
      <AppHeader
        title={m.company_name || m.customer_name}
        back
        right={<Pill label={m.status} tone={m.status === "completed" ? "success" : m.status === "cancelled" ? "danger" : "brand"} />}
      />
      <div className="sg-main sg-fade-in">
        <div className="sg-card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.4 }} data-testid="meeting-date-display">{formatDate(m.date)}</div>
          <div style={{ fontSize: 14, color: "var(--muted)" }}>{m.time}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <span style={tag}>{String(m.meeting_type).replace("_", " ")}</span>
            <span style={tag}>{m.team}</span>
          </div>
          {m.google_meet_link && (
            <a href={m.google_meet_link} target="_blank" rel="noreferrer" className="sg-btn" style={{ marginTop: 12, width: "100%" }} data-testid="meeting-join">
              <Video size={16} /> Join Google Meet
            </a>
          )}
          {m.status !== "cancelled" && !rescheduling && (
            <button className="sg-btn" onClick={startReschedule} data-testid="meeting-reschedule" style={{ marginTop: 8, width: "100%" }}>
              <CalendarClock size={16} color="var(--brand)" /> Reschedule
            </button>
          )}
        </div>

        {rescheduling && (
          <div className="sg-card" style={{ marginBottom: 14 }} data-testid="meeting-reschedule-panel">
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 8 }}>Reschedule meeting</div>
            <Field
              label="New date"
              type="date"
              value={rDate}
              onChange={(e) => { setRDate(e.target.value); setRTime(""); }}
              min={new Date().toISOString().slice(0, 10)}
              data-testid="reschedule-date"
            />
            <label className="sg-label">New time</label>
            {slots.allowed.length === 0 ? (
              <div style={{ color: "var(--muted)", fontSize: 13, padding: "8px 0" }}>
                We&apos;re closed on this day. Please pick a weekday (Mon–Fri).
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 12 }}>
                {slots.allowed.map((s) => {
                  const isTaken = taken.has(s);
                  const active = rTime === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={isTaken}
                      onClick={() => setRTime(s)}
                      className={`sg-slot ${active ? "sg-slot-active" : ""} ${isTaken ? "sg-slot-taken" : ""}`}
                      data-testid={`reschedule-slot-${s}`}
                      style={{ minWidth: 0 }}
                    >{s}</button>
                  );
                })}
              </div>
            )}
            {rError && <div style={{ color: "var(--danger)", fontSize: 13, marginBottom: 8 }}>{rError}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <button className="sg-btn" onClick={() => setRescheduling(false)} data-testid="reschedule-cancel">Cancel</button>
              <button className="sg-btn sg-btn-primary" onClick={submitReschedule} disabled={rBusy} data-testid="reschedule-confirm">
                {rBusy ? "Updating..." : "Confirm reschedule"}
              </button>
            </div>
          </div>
        )}

        <div className="sg-card" style={{ marginBottom: 14 }}>
          {[["Contact", m.customer_name], ["Email", m.customer_email], ["Phone", m.customer_phone], ["Source", m.source === "public_form" ? "Scheduler link" : "Manual"]]
            .map(([l, v]) => (
              <div key={l} style={{ display: "flex", padding: "8px 0", borderTop: "1px solid var(--line)" }}>
                <div style={{ width: 90, fontSize: 10, color: "var(--muted)", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.6 }}>{l}</div>
                <div style={{ flex: 1, fontSize: 14, wordBreak: "break-word" }}>{v || "—"}</div>
              </div>
            ))}
        </div>

        <div className="sg-section-h">Status</div>
        <div className="sg-chip-row" style={{ marginBottom: 16 }}>
          {STATUSES.map((st) => (
            <button key={st} className={`sg-chip ${m.status === st ? "sg-chip-active" : ""}`} onClick={() => update({ status: st })} data-testid={`meeting-status-${st}`}>{st}</button>
          ))}
        </div>

        <TextArea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="meeting-notes-edit" />
        <TextArea label="Summary" value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="meeting-summary-edit" />

        <button className="sg-btn sg-btn-primary" onClick={() => update({ notes, summary })} disabled={saving} data-testid="meeting-save-notes" style={{ width: "100%", marginTop: 12 }}>
          {saving ? "Saving..." : "Save changes"}
        </button>
        {m.status !== "cancelled" && (
          <button className="sg-btn sg-btn-danger" onClick={cancel} data-testid="meeting-cancel" style={{ width: "100%", marginTop: 10 }}>
            <X size={16} /> Cancel meeting
          </button>
        )}
      </div>
    </>
  );
}

const tag = { display: "inline-flex", alignItems: "center", background: "var(--surface-subtle)", color: "var(--muted)", padding: "3px 8px", borderRadius: 6, fontSize: 11, textTransform: "capitalize" };
